if (!customElements.get('okendo-community-reviews')) {
  class OkendoCommunityReviews extends HTMLElement {
    connectedCallback() {
      this.loadController?.abort();
      this.loadController = new AbortController();
      this.loadId = (this.loadId || 0) + 1;
      const loadId = this.loadId;
      const { signal } = this.loadController;

      this.apiBase = this.dataset.apiBase;
      this.displayLimit = Number(this.dataset.displayLimit);
      this.locale = this.dataset.locale || document.documentElement.lang || undefined;
      this.viewport = this.querySelector('[data-okendo-reviews-viewport]');
      this.track = this.querySelector('[data-okendo-reviews-track]');
      this.controls = this.querySelector('[data-okendo-reviews-controls]');
      this.previousButton = this.querySelector('[data-okendo-reviews-previous]');
      this.nextButton = this.querySelector('[data-okendo-reviews-next]');
      this.countElement = this.closest('.okendo-community-reviews').querySelector('[data-okendo-review-count]');

      this.loadReviews(loadId, signal);
      this.loadReviewCount(loadId, signal);
    }

    disconnectedCallback() {
      // Invalidate this generation even if a fetch implementation resolves after being aborted.
      this.loadId = (this.loadId || 0) + 1;
      this.loadController?.abort();
      this.loadController = null;
      this.destroyCarousel();
    }

    isCurrentLoad(loadId) {
      return this.isConnected && this.loadId === loadId;
    }

    async fetchOkendo(url, signal) {
      // Okendo's public endpoint currently rejects CORS preflights for its documented version header.
      // Omitting that non-simple header keeps this storefront request CORS-safe and returns the current API version.
      const response = await fetch(url, { signal });
      if (!response.ok) throw new Error(`Okendo request failed: ${response.status}`);
      return response.json();
    }

    async loadReviewCount(loadId, signal) {
      try {
        const { reviewAggregate } = await this.fetchOkendo(`${this.apiBase}/review_aggregate`, signal);
        if (!this.isCurrentLoad(loadId)) return;
        const count = reviewAggregate && reviewAggregate.reviewCount;
        if (!Number.isFinite(count)) return;

        this.countElement.textContent = `from ${new Intl.NumberFormat(this.locale).format(count)} reviews`;
        this.countElement.hidden = false;
      } catch (error) {
        if (signal.aborted || !this.isCurrentLoad(loadId)) return;
        // The reviews remain useful when the non-essential aggregate request is unavailable.
        console.warn('Unable to load Okendo review aggregate.', error);
      }
    }

    async loadReviews(loadId, signal) {
      try {
        const reviews = await this.collectFiveStarReviews(signal);
        if (!this.isCurrentLoad(loadId) || !reviews.length) return;

        this.destroyCarousel();
        this.track.replaceChildren(...reviews.map((review) => this.createReviewCard(review)));
        if (!this.isCurrentLoad(loadId)) return;
        this.initializeCarousel(reviews.length, loadId);
      } catch (error) {
        if (signal.aborted || !this.isCurrentLoad(loadId)) return;
        // Keep the section empty on failure rather than showing incomplete or misleading cards.
        console.warn('Unable to load Okendo community reviews.', error);
      }
    }

    async collectFiveStarReviews(signal) {
      const eligibleReviews = [];
      let url = `${this.apiBase}/reviews?limit=25&orderBy=date%20desc`;
      // A sparse set of eligible reviews should not turn this into an unbounded API scan.
      const maxPages = 10;
      let pagesScanned = 0;

      while (url && eligibleReviews.length < this.displayLimit && pagesScanned < maxPages) {
        const { reviews = [], nextUrl } = await this.fetchOkendo(url, signal);
        pagesScanned += 1;
        reviews.forEach((review) => {
          if (
            review.rating === 5 &&
            review.reviewer?.isVerified === true &&
            review.productImageUrl &&
            eligibleReviews.length < this.displayLimit
          ) {
            eligibleReviews.push(review);
          }
        });

        // Okendo returns pagination links relative to /v1, not to the current store URL.
        url = !signal.aborted && nextUrl ? `https://api.okendo.io/v1${nextUrl}` : null;
      }

      return eligibleReviews;
    }

    createReviewCard(review) {
      const slide = document.createElement('article');
      slide.className = 'okendo-community-reviews__slide swiper-slide';

      const card = document.createElement('div');
      card.className = 'okendo-community-reviews__card';

      const image = document.createElement('img');
      image.className = 'okendo-community-reviews__image';
      image.src = this.getHighResolutionImageUrl(review.productImageUrl);
      image.alt = review.productName ? `${review.productName} product image` : '';
      image.loading = 'lazy';
      image.decoding = 'async';
      card.append(image);

      const content = document.createElement('div');
      content.className = 'okendo-community-reviews__content';
      const stars = this.createTextElement('div', 'okendo-community-reviews__stars', '★★★★★');
      stars.setAttribute('role', 'img');
      stars.setAttribute('aria-label', '5 out of 5 stars');
      content.append(stars);

      const reviewer = document.createElement('div');
      reviewer.className = 'okendo-community-reviews__reviewer';
      reviewer.append(this.createTextElement('span', '', review.reviewer?.displayName || 'Verified customer'));
      if (review.reviewer?.isVerified) reviewer.append(this.createTextElement('span', 'okendo-community-reviews__verified', 'Verified'));
      content.append(reviewer);

      if (review.title && review.title !== '5 Stars') content.append(this.createTextElement('h3', 'okendo-community-reviews__title', review.title));
      if (review.body) content.append(this.createTextElement('p', 'okendo-community-reviews__body', review.body));

      const meta = document.createElement('div');
      meta.className = 'okendo-community-reviews__meta';
      if (review.productName) {
        const productUrl = this.getSafeProductUrl(review.productUrl);
        const product = this.createTextElement(productUrl ? 'a' : 'span', productUrl ? 'okendo-community-reviews__product-link' : '', review.productName);
        if (productUrl) product.href = productUrl;
        meta.append(product);
      }
      if (review.dateCreated) {
        const date = this.createTextElement('time', 'okendo-community-reviews__date', this.formatDate(review.dateCreated));
        date.dateTime = review.dateCreated;
        meta.append(date);
      }

      content.append(meta);
      card.append(content);
      slide.append(card);
      return slide;
    }

    getSafeProductUrl(productUrl) {
      if (typeof productUrl !== 'string' || !productUrl.trim()) return null;

      try {
        const storefrontOrigin = new URL(window.location.origin);
        const resolvedUrl = new URL(productUrl, storefrontOrigin);

        // Links must remain on this HTTPS storefront; retain protocol-relative values for compatible storefront URLs.
        if (resolvedUrl.protocol !== 'https:' || resolvedUrl.origin !== storefrontOrigin.origin) return null;
        return productUrl.trim().startsWith('//') ? productUrl.trim() : resolvedUrl.href;
      } catch {
        return null;
      }
    }

    createTextElement(tagName, className, text) {
      const element = document.createElement(tagName);
      if (className) element.className = className;
      element.textContent = text;
      return element;
    }

    getHighResolutionImageUrl(imageUrl) {
      // Okendo supplies Shopify's 280px rendition; cards render larger, so request the matching 720px CDN rendition.
      return imageUrl.replace(/_x\d+(?=\.[a-z]+(?:\?|$))/i, '_x720');
    }

    formatDate(date) {
      return new Intl.DateTimeFormat(this.locale, { day: '2-digit', month: '2-digit', year: 'numeric' }).format(new Date(date));
    }

    initializeCarousel(count, loadId) {
      if (count > 1 && window.FoxTheme?.Carousel && this.isCurrentLoad(loadId)) {
        const carousel = new window.FoxTheme.Carousel(this.viewport, {
          slidesPerView: 'auto',
          spaceBetween: 6,
          centeredSlides: true,
          loop: count > 4,
          threshold: 2,
          navigation: { nextEl: this.nextButton, prevEl: this.previousButton },
          breakpoints: {
            768: { slidesPerView: 2, spaceBetween: 28, centeredSlides: false },
            1024: { slidesPerView: 4, spaceBetween: 28, centeredSlides: false },
          },
        });
        carousel.init();
        if (!this.isCurrentLoad(loadId)) {
          if (carousel.slider) carousel.slider.destroy(true, true);
          return;
        }
        this.carousel = carousel;
        this.controls.hidden = false;
      }
    }

    destroyCarousel() {
      if (this.carousel?.slider) this.carousel.slider.destroy(true, true);
      this.carousel = null;
      if (this.controls) this.controls.hidden = true;
    }
  }

  customElements.define('okendo-community-reviews', OkendoCommunityReviews);
}
